/**
 * Transport boundary.
 *
 * Talks to the PlayInvictus API (NestJS) when one is configured, and to the
 * bundled fixtures otherwise. Services declare a logical path *and* a fallback;
 * this module decides which to use and where the path actually lives.
 */

/** Origin only, e.g. http://localhost:3000 — prefixes are applied per-path. */
const API_ORIGIN = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

/** True once an API origin is configured. */
export const isLiveBackend = API_ORIGIN !== '';

/**
 * The API serves admin routes off a different root from player routes, and
 * excludes them from its global prefix:
 *   player  /api/v1/...
 *   admin   /admin/api/v1/...
 *
 * Services keep using logical paths (`/players`, `/admin/users`); the mapping
 * lives here so a routing change on the API is a one-line edit.
 */
const PLAYER_PREFIX = '/api/v1';
const ADMIN_PREFIX = '/admin/api/v1';

function resolveUrl(path: string): string {
  if (path.startsWith('/admin/')) {
    return `${API_ORIGIN}${ADMIN_PREFIX}${path.slice('/admin'.length)}`;
  }
  return `${API_ORIGIN}${PLAYER_PREFIX}${path}`;
}

/**
 * The absolute URL a logical path resolves to.
 *
 * Needed by flows the browser navigates to rather than fetches — Google
 * sign-in hands the whole tab to the API, so it cannot go through `fetch`.
 */
export const apiUrl = (path: string): string => resolveUrl(path);

/** Simulated latency for fixtures, so loading states are exercised in development. */
const MOCK_LATENCY_MS = 120;

/**
 * How long a request may hang before it counts as unreachable. Without this a
 * server that accepts the connection and then stalls would hold the screen on
 * its spinner indefinitely.
 */
const REQUEST_TIMEOUT_MS = 8_000;

/**
 * How long to stop trying after the API proves unreachable.
 *
 * Every screen fires several requests. Without a cooldown each one would wait
 * out its own timeout, so a stopped server would make the demo crawl rather
 * than fall back cleanly.
 */
const OFFLINE_COOLDOWN_MS = 15_000;

/**
 * Where each app keeps its session.
 *
 * The player app and the console are served from the same origin, so they
 * share localStorage. With one set of keys, signing into the console replaced
 * the player's session and a player logout signed the console out. Each app
 * now has its own; the player keys are the original ones, so sessions from
 * before the split survive.
 */
export type TokenNamespace = 'player' | 'admin';

const TOKEN_KEYS: Record<TokenNamespace, { access: string; refresh: string; mode: string }> = {
  player: {
    access: 'matchday-token',
    refresh: 'matchday-refresh-token',
    mode: 'matchday-session-mode',
  },
  admin: {
    access: 'matchday-admin-token',
    refresh: 'matchday-admin-refresh-token',
    mode: 'matchday-admin-session-mode',
  },
};

let tokenNamespace: TokenNamespace = 'player';
let tokenKeys = TOKEN_KEYS.player;

/**
 * Point the token store at one app's keys and load whatever session is there.
 *
 * Called once by an entry point before its first request; the player app needs
 * no call, since its keys are the default.
 */
export function setTokenNamespace(namespace: TokenNamespace): void {
  tokenNamespace = namespace;
  tokenKeys = TOKEN_KEYS[namespace];
  loadAuthToken();
}

/* ------------------------------------------------------------------ *
 * Backend readiness
 * ------------------------------------------------------------------ */

/**
 * Paths the API actually implements today.
 *
 * The backend is being built incrementally, so pointing the app at it would
 * otherwise 404 every screen. Anything not listed keeps using its fixture even
 * when an origin is set.
 *
 * **As the backend team ships an endpoint, add its path prefix here.** That one
 * line is the whole handover — the service and every call site already exist.
 */
const IMPLEMENTED_PATHS: readonly string[] = [
  '/health',
  '/admin/auth',
  '/admin/users',
  '/admin/notifications',
  '/admin/waitlist',
  '/admin/admin-users',
  '/admin/football',
  '/admin/contests',
  '/admin/scoring',
  '/admin/jobs',
  '/admin/audit',
  '/admin/analytics',
  '/admin/provider',
  '/admin/players',
  '/auth',
  '/contests',
  '/managers/me',
  '/me/contests',
  '/me/notifications',
  '/me/leagues',
  '/me/teams',
  '/leagues',
  '/football',
  '/scoring',
];

/** Does the API implement this path yet? */
export function isImplemented(path: string): boolean {
  const [pathname] = path.split('?');
  if (pathname === undefined) return false;

  return IMPLEMENTED_PATHS.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * True when a given path will hit the network rather than a fixture.
 *
 * A known-unreachable API is skipped outright: every screen fires several
 * requests, and waiting out a timeout on each one would make an offline demo
 * far slower than one with no backend configured at all.
 */
export const isPathLive = (path: string): boolean =>
  isLiveBackend && !isBackendOffline() && isImplemented(path);

/* ------------------------------------------------------------------ *
 * Offline fallback
 * ------------------------------------------------------------------ *
 *
 * This is a demo build: the API is still being written, and it is routinely
 * not running at all. Rather than showing an error screen, an unreachable API
 * falls back to the same fixtures the app uses with no origin configured, so
 * the product can always be walked through end to end.
 *
 * A *reachable* API is always believed. A 401 or a 400 is a real answer and is
 * surfaced as one — only the server being absent triggers the fallback.
 */

let offlineUntil = 0;

/** True while the API is being treated as unreachable. */
export const isBackendOffline = (): boolean => isLiveBackend && Date.now() < offlineUntil;

const offlineListeners = new Set<(offline: boolean) => void>();

/** Subscribe to reachability changes, so the UI can say it is on demo data. */
export function onBackendOfflineChange(listener: (offline: boolean) => void): () => void {
  offlineListeners.add(listener);
  return () => offlineListeners.delete(listener);
}

function setOffline(offline: boolean): void {
  const was = isBackendOffline();
  offlineUntil = offline ? Date.now() + OFFLINE_COOLDOWN_MS : 0;

  if (was !== offline) offlineListeners.forEach((listener) => listener(offline));
}

/**
 * Is this failure the server being absent, rather than the server answering?
 *
 * `TypeError` is what fetch throws for a refused connection, DNS failure or a
 * blocked CORS preflight. A 502/503/504 means a proxy is up but nothing is
 * behind it, and a timeout aborts with our own reason.
 */
function isUnreachable(cause: unknown): boolean {
  if (cause instanceof TypeError) return true;
  if (cause instanceof ApiError) return cause.status === 0 || cause.status >= 502;
  return false;
}

/* ------------------------------------------------------------------ *
 * Errors
 * ------------------------------------------------------------------ */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The API's `error` field, e.g. "Unauthorized", "Bad Request". */
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** `ApiError.code` for a route the connected API does not implement at all. */
export const ENDPOINT_MISSING = 'ENDPOINT_MISSING';

const ENDPOINT_MISSING_MESSAGE = "This part of the API isn't available on this server yet.";

/** Is this the API saying it has no such route, rather than no such record? */
export const isEndpointMissing = (cause: unknown): boolean =>
  cause instanceof ApiError && cause.code === ENDPOINT_MISSING;

/* ------------------------------------------------------------------ *
 * Tokens
 * ------------------------------------------------------------------ */

/**
 * How the session is carried.
 *
 * `bearer` — mobile OTP. The API hands back Cognito tokens in the response
 * body and every request carries `Authorization`. Works against a wildcard
 * `CORS_ORIGIN`, because nothing credentialed crosses the origin boundary.
 *
 * `cookie` — Google. PKCE lives on the API, so the tokens are never given to
 * the browser: the callback sets HTTP-only `pi_at`/`pi_rt` on the API origin
 * and the app rides those. That needs `credentials: 'include'`, which in turn
 * needs the API to name this exact origin in `CORS_ORIGIN` — a wildcard makes
 * the browser drop the response before it is read.
 *
 * The mode is therefore per-session rather than global: a bearer session must
 * not pay the CORS cost of a cookie one it never uses.
 */
export type SessionMode = 'bearer' | 'cookie';

let sessionMode: SessionMode = 'bearer';
let accessToken: string | null = null;
let refreshToken: string | null = null;

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string | null): void => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Private browsing can refuse storage; the in-memory copy still works.
  }
};

/** Restore the session across reloads. Safe to call before any request. */
export function loadAuthToken(): void {
  accessToken = readStorage(tokenKeys.access);
  refreshToken = readStorage(tokenKeys.refresh);
  sessionMode = readStorage(tokenKeys.mode) === 'cookie' ? 'cookie' : 'bearer';
}

export function setAuthToken(token: string | null, refresh?: string | null): void {
  accessToken = token;
  writeStorage(tokenKeys.access, token);

  if (refresh !== undefined) {
    refreshToken = refresh;
    writeStorage(tokenKeys.refresh, refresh);
  }
}

/**
 * Record that the session is carried by the API's cookies rather than a token.
 *
 * Nothing about the cookies themselves is stored — they are HTTP-only and
 * invisible here. This is only the app remembering to ask for them.
 */
export function setSessionMode(mode: SessionMode): void {
  sessionMode = mode;
  writeStorage(tokenKeys.mode, mode === 'cookie' ? 'cookie' : null);
}

export const getSessionMode = (): SessionMode => sessionMode;

export function clearAuthTokens(): void {
  setAuthToken(null, null);
  setSessionMode('bearer');
}

export const getAuthToken = (): string | null => accessToken;
export const getRefreshToken = (): string | null => refreshToken;
export const hasRefreshToken = (): boolean => refreshToken !== null;

/**
 * Is there anything to authenticate with?
 *
 * A cookie session has no token to inspect, so its presence is inferred from
 * the mode — the API is the one that decides whether the cookie is still good.
 */
export const hasSession = (): boolean => accessToken !== null || sessionMode === 'cookie';

/** Called when a refresh fails, so the app can send the user back to sign in. */
let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

/** `ApiError.code` when an admin must set a new password before anything else. */
export const PASSWORD_CHANGE_REQUIRED = 'PASSWORD_CHANGE_REQUIRED';

/**
 * Called when the API refuses a console request until the password changes.
 *
 * An account created by another admin starts with a password someone else
 * chose. The API allows only `/auth/me` and `/auth/change-password` until it is
 * replaced, and says so with a 403 carrying `PASSWORD_CHANGE_REQUIRED` — which
 * any request can be the first to discover.
 */
let onPasswordChangeRequired: (() => void) | null = null;

export function setPasswordChangeRequiredHandler(handler: (() => void) | null): void {
  onPasswordChangeRequired = handler;
}

/* ------------------------------------------------------------------ *
 * Envelope
 * ------------------------------------------------------------------ */

/**
 * The API wraps every response:
 *   success: { success: true,  statusCode, message, data, timestamp, path }
 *   failure: { success: false, statusCode, message, error, timestamp, path }
 *
 * `message` may be a string or an array of strings — class-validator returns
 * one entry per failed constraint.
 */
interface Envelope {
  success?: boolean;
  data?: unknown;
  message?: string | string[];
  error?: string;
}

const isEnvelope = (value: unknown): value is Envelope =>
  typeof value === 'object' && value !== null && 'success' in value;

const flattenMessage = (message: string | string[] | undefined): string | null => {
  if (typeof message === 'string') return message;
  if (Array.isArray(message) && message.length > 0) return message.join(' ');
  return null;
};

/**
 * Unwrap a success envelope to its payload.
 *
 * Endpoints marked `@SkipResponseWrap()` return bare JSON, so an unwrapped body
 * passes through rather than being treated as malformed.
 */
function unwrap<T>(body: unknown): T {
  if (isEnvelope(body) && 'data' in body) return body.data as T;
  return body as T;
}

/** A paginated list, as the API returns them. */
export interface Paginated<T> {
  items: T[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

/* ------------------------------------------------------------------ *
 * Requests
 * ------------------------------------------------------------------ */

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

function mockRequest<T>(data: T, signal?: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timer = setTimeout(() => resolve(data), MOCK_LATENCY_MS);

    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

async function describeFailure(response: Response): Promise<ApiError> {
  try {
    const body: unknown = await response.json();

    if (isEnvelope(body)) {
      const message = flattenMessage(body.message);

      // Nest's own answer for a route it does not have. Pointed at a server
      // behind the branch this app was built against, whole sections 404 this
      // way — that is "not deployed here yet", not a fault worth alarming over.
      if (response.status === 404 && message && /^Cannot [A-Z]+ \//.test(message)) {
        console.warn(`[api] ${message}`);
        return new ApiError(ENDPOINT_MISSING_MESSAGE, 404, ENDPOINT_MISSING);
      }

      if (message) return new ApiError(message, response.status, body.error);
    }
  } catch {
    // Non-JSON error body; fall through to the status text.
  }

  return new ApiError(
    response.statusText || `Request failed (${response.status})`,
    response.status,
  );
}

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal | undefined;
}

async function sendRequest(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', body, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  // Cookies are asked for only by a session that has them. A bearer session
  // stays uncredentialed so it keeps working against a wildcard CORS_ORIGIN,
  // which cannot answer a credentialed request.
  //
  // Admin routes are never cookie-backed — the console signs in against the
  // API's own JWTs. They are excluded explicitly because the two apps share
  // this origin's storage, so a player's Google session must not make the
  // console start sending credentials the API is not set up to accept.
  const credentialed = sessionMode === 'cookie' && !path.startsWith('/admin/');

  return fetch(resolveUrl(path), {
    method,
    headers,
    credentials: credentialed ? 'include' : 'same-origin',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    signal: withTimeout(signal),
  });
}

/**
 * The caller's signal, plus a deadline.
 *
 * `AbortSignal.any` keeps the caller's cancellation working — losing it would
 * leave requests running after their screen unmounted.
 */
function withTimeout(signal: AbortSignal | undefined): AbortSignal {
  const deadline = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  return signal ? AbortSignal.any([signal, deadline]) : deadline;
}

/**
 * Endpoints that establish or end a session rather than consume one.
 *
 * A 401 from these is the answer to the request — a wrong code, a spent
 * refresh token — so retrying them behind a refresh would only replace a clear
 * error message with a silent sign-out.
 */
const CREDENTIAL_EXCHANGE_PATHS: readonly string[] = [
  '/auth/mobile',
  '/auth/refresh',
  '/auth/logout',
  '/auth/session',
  '/admin/auth/login',
  '/admin/auth/refresh',
];

const isCredentialExchange = (path: string): boolean =>
  CREDENTIAL_EXCHANGE_PATHS.some((prefix) => path.startsWith(prefix));

/** Did this abort come from our deadline rather than the caller? */
const isTimeout = (cause: unknown): boolean =>
  cause instanceof DOMException && cause.name === 'TimeoutError';

/**
 * How to trade the stored refresh token for a fresh access token.
 *
 * The two apps sharing this transport refresh against different endpoints —
 * players at `/auth/refresh`, the console at `/admin/auth/refresh` — with
 * different payloads, so each registers its own on boot. Unregistered, a 401
 * simply ends the session.
 *
 * Registered per token namespace, because the console's bundle also loads the
 * player auth store (through shared layout components), which registers the
 * player refresher as a side effect. Keyed this way, whichever registers last
 * cannot hand the console's refresh token to the player endpoint.
 */
export type TokenRefresher = () => Promise<boolean>;

const tokenRefreshers: Record<TokenNamespace, TokenRefresher | null> = {
  player: null,
  admin: null,
};

export function setTokenRefresher(
  refresher: TokenRefresher | null,
  namespace: TokenNamespace = 'player',
): void {
  tokenRefreshers[namespace] = refresher;
}

/**
 * Single-flight token refresh.
 *
 * Access tokens are short-lived, so an idle tab will hit 401s in normal use.
 * Concurrent requests share one refresh rather than each firing their own and
 * racing to overwrite the token.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  // A cookie session has no refresh token here to spend — `pi_rt` is HTTP-only
  // and the refresh endpoint reads the token from the body, not the cookie.
  const tokenRefresher = tokenRefreshers[tokenNamespace];
  if (tokenRefresher === null || refreshToken === null) return false;

  refreshInFlight ??= (async () => {
    try {
      return await tokenRefresher();
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response;

  try {
    response = await sendRequest(path, options);
  } catch (cause) {
    // A refused connection or a blown deadline is the server being absent, not
    // an answer. Everything else — including the caller cancelling — is real.
    if (cause instanceof TypeError || isTimeout(cause)) {
      setOffline(true);
      throw new ApiError('Could not reach the server.', 0);
    }
    throw cause;
  }

  // Reaching Nest at all clears a previous outage, even if it answers 4xx.
  setOffline(false);

  // A 401 on anything other than the credential exchanges themselves means the
  // access token has aged out. Refresh once and retry; a second failure is
  // real. A rejected OTP or a bad password is an answer, not an expiry.
  if (response.status === 401 && !isCredentialExchange(path)) {
    if (await refreshAccessToken()) {
      response = await sendRequest(path, options);
    } else {
      clearAuthTokens();
      onSessionExpired?.();
    }
  }

  if (!response.ok) {
    const failure = await describeFailure(response);
    if (failure.status === 403 && failure.code === PASSWORD_CHANGE_REQUIRED) {
      onPasswordChangeRequired?.();
    }
    throw failure;
  }

  if (response.status === 204) return undefined as T;

  return unwrap<T>(await response.json());
}

/* ------------------------------------------------------------------ *
 * Service helpers
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ *
 * Empty-response fallback
 * ------------------------------------------------------------------ */

/**
 * Stand in for an endpoint that answers, but with nothing in it.
 *
 * The API is live well ahead of its data: series, contests and fixtures are
 * seeded piecemeal, so a screen can be wired correctly and still render as a
 * row of dashes. For a walkthrough that reads as broken, so an empty answer
 * falls back to the same fixture an absent API would use — and the moment real
 * rows exist, they win and the fixture disappears on its own.
 *
 * Only *emptiness* triggers it. A populated response is never touched, and a
 * refusal is still a refusal.
 *
 * Set `VITE_DEMO_FALLBACK=false` to turn this off and let empty mean empty,
 * which is what a production build should do.
 */
const DEMO_FALLBACK = import.meta.env.VITE_DEMO_FALLBACK !== 'false';

/** Whether the current screen is showing any stand-in data. */
let usingFixtures = false;

const fixtureListeners = new Set<(using: boolean) => void>();

/** True while a fixture is standing in for an empty or absent API response. */
export const isUsingFixtures = (): boolean => usingFixtures;

/** Subscribe to fixture-substitution changes, so the UI can own up to it. */
export function onFixtureUseChange(listener: (using: boolean) => void): () => void {
  fixtureListeners.add(listener);
  return () => fixtureListeners.delete(listener);
}

function setUsingFixtures(using: boolean): void {
  if (usingFixtures === using) return;
  usingFixtures = using;
  fixtureListeners.forEach((listener) => listener(using));
}

/**
 * Did the API answer with nothing?
 *
 * Deliberately narrow. `0`, `false` and `''` are answers — a rank of zero is
 * data — so only a missing value, an empty collection, or a page with no items
 * counts as nothing to show.
 */
function isEmptyResponse(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;

  if (typeof value === 'object') {
    const items = (value as { items?: unknown }).items;
    if (Array.isArray(items)) return items.length === 0;
  }

  return false;
}

/**
 * A read. Hits the API when the path is implemented and an origin is
 * configured, otherwise resolves the bundled fixture.
 *
 * The fallback is a thunk so mock data is only built when it is actually used.
 */
export function read<T>(
  path: string,
  fallback: () => T,
  signal?: AbortSignal,
  options: {
    /**
     * Set false where an empty answer is meaningful and must be shown as
     * empty — e.g. a standings period with no contest behind it — rather than
     * replaced by demo data.
     */
    fallbackWhenEmpty?: boolean;
  } = {},
): Promise<T> {
  if (!isPathLive(path)) {
    setUsingFixtures(true);
    return mockRequest(fallback(), signal);
  }

  const { fallbackWhenEmpty = true } = options;

  return withFixtureFallback(
    apiRequest<T>(path, { signal }).then((data) => {
      if (DEMO_FALLBACK && fallbackWhenEmpty && isEmptyResponse(data)) {
        setUsingFixtures(true);
        return fallback();
      }

      return data;
    }),
    fallback,
  );
}

/**
 * A write. Hits the API when the path is implemented; otherwise resolves the
 * optimistic result so the demo behaves exactly like the live app.
 *
 * Unlike a read, a write against a configured API never falls back to the
 * fixture — not when the server is down, not during the offline cooldown. A
 * fake success there is a lie with consequences: an OTP request that reported
 * "Code sent by SMS" when nothing was sent, an entry that was never saved. The
 * failure is surfaced as an `ApiError` instead.
 */
export function write<T>(
  path: string,
  method: Exclude<HttpMethod, 'GET'>,
  body: unknown,
  fallback: () => T,
  signal?: AbortSignal,
): Promise<T> {
  if (!isLiveBackend || !isImplemented(path)) return mockRequest(fallback(), signal);
  return apiRequest<T>(path, { method, body, signal });
}

/**
 * Serve the fixture when the API turns out to be unreachable.
 *
 * Only absence is caught. A server that answers — 400, 401, 404, 409 — has
 * told us something true, and hiding that behind demo data would turn a real
 * bug into a screen that looks like it works.
 */
async function withFixtureFallback<T>(request: Promise<T>, fallback: () => T): Promise<T> {
  try {
    return await request;
  } catch (cause) {
    if (!isUnreachable(cause)) throw cause;
    setUsingFixtures(true);
    return fallback();
  }
}

/**
 * Did the API say the thing asked for does not exist?
 *
 * A malformed id in the URL comes back as a 400 ("Invalid ID format") rather
 * than a 404, but to someone who followed a stale or mistyped link the two are
 * the same answer. Network failures and 5xx are not included — those deserve a
 * retry, not a dead end.
 */
export const isNotFoundError = (cause: unknown): boolean =>
  cause instanceof ApiError &&
  // 403 too: someone else's team id ("Not your team") is, to this player, a
  // team that does not exist — and saying more would confirm that it does.
  (cause.status === 400 || cause.status === 403 || cause.status === 404) &&
  // A route the server lacks says nothing about whether the record exists.
  cause.code !== ENDPOINT_MISSING;

/**
 * Did the API refuse this for the caller's role?
 *
 * Not a fault: the console's permission map can lag the API's guards, and a
 * section can draw on an endpoint a role cannot read.
 */
export const isForbiddenError = (cause: unknown): boolean =>
  cause instanceof ApiError && cause.status === 403;

/** Turn any thrown value into something worth showing a user. */
export function describeError(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;
  if (cause instanceof DOMException && cause.name === 'AbortError') return 'Request cancelled.';
  if (cause instanceof TypeError) return 'Could not reach the server. Check your connection.';
  if (cause instanceof Error) return cause.message;
  return 'Something went wrong.';
}
